import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Calendar, DollarSign, FileText, Loader2 } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import Input from '../../../../components/ui/Input';
import Button from '../../../../components/ui/Button';
import MultiSelectDropdown from '../../../../components/ui/MultiSelectDropdown';
import { appointmentService } from '../../../../services/appointmentService';
import { wellnessService } from '../../../../services/wellnessService';
import { staffService } from '../../../../services/staffService';
import { clientService } from '../../../../services/clientService';
import { calculateConsultationTotal, calculateWellnessTotal, formatPriceBreakdown } from '../../../../utils/pricingUtils';

const AdminBookingModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form data
  const [bookingType, setBookingType] = useState('consultation'); // consultation, service, treatment
  const [formData, setFormData] = useState({
    // Client details
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    clientGender: '',

    // Booking details
    chiefComplaint: '',
    medicalHistory: '',
    currentMedications: '',
    severity: 'moderate',
    consultationMode: 'in_person',
    notes: '',

    // Date & Time
    preferredDate: '',
    preferredTime: '',

    // Payment
    paymentMethod: 'clinic',
    paymentStatus: 'pending',
    fee: 0,

    // Type-specific
    doctorId: null,
    serviceIds: [],
    treatmentId: null,
    therapistIds: [] // Array of selected therapist IDs
  });

  // Dropdown options
  const [doctors, setDoctors] = useState([]);
  const [services, setServices] = useState([]);
  const [treatments, setTreatments] = useState([]);

  // Client search/autocomplete
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [searchingClients, setSearchingClients] = useState(false);

  // Therapist selection
  const [availableTherapists, setAvailableTherapists] = useState([]);
  const [therapistsRequired, setTherapistsRequired] = useState(1);
  const [loadingTherapists, setLoadingTherapists] = useState(false);
  const [therapistWarning, setTherapistWarning] = useState('');

  // Time slot validation
  const [clinicHours, setClinicHours] = useState(null);
  const [timeSlotWarning, setTimeSlotWarning] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadDropdownData();
      loadClinicHours();
    }
  }, [isOpen]);

  // Load clinic hours for time validation
  const loadClinicHours = async () => {
    try {
      const { data, error } = await supabase
        .from('clinic_business_hours')
        .select('*')
        .order('day_of_week');

      if (error) throw error;
      setClinicHours(data || []);
    } catch (error) {
      console.error('Error loading clinic hours:', error);
    }
  };

  // Validate selected time against clinic hours
  const validateTimeSlot = (date, time) => {
    if (!date || !time || !clinicHours || clinicHours.length === 0) {
      setTimeSlotWarning('');
      return;
    }

    const selectedDate = new Date(date);
    const dayOfWeek = selectedDate.getDay();
    const dayHours = clinicHours.find(h => h.day_of_week === dayOfWeek);

    if (!dayHours) {
      setTimeSlotWarning('⚠️ No clinic hours configured for this day');
      return;
    }

    if (!dayHours.is_open) {
      setTimeSlotWarning('⚠️ Clinic is closed on this day');
      return;
    }

    const selectedTime = time + ':00'; // Convert HH:MM to HH:MM:SS

    if (selectedTime < dayHours.opening_time || selectedTime >= dayHours.closing_time) {
      setTimeSlotWarning(`⚠️ Selected time is outside clinic hours (${dayHours.opening_time.slice(0, 5)} - ${dayHours.closing_time.slice(0, 5)})`);
      return;
    }

    // Check if time falls during break
    if (dayHours.break_start_time && dayHours.break_end_time) {
      if (selectedTime >= dayHours.break_start_time && selectedTime < dayHours.break_end_time) {
        setTimeSlotWarning(`⚠️ Selected time is during break hours (${dayHours.break_start_time.slice(0, 5)} - ${dayHours.break_end_time.slice(0, 5)})`);
        return;
      }
    }

    setTimeSlotWarning('');
  };

  // Validate time whenever date or time changes
  useEffect(() => {
    validateTimeSlot(formData.preferredDate, formData.preferredTime);
  }, [formData.preferredDate, formData.preferredTime, clinicHours]);

  const loadDropdownData = async () => {
    try {
      console.log('🔄 Loading dropdown data...');

      // Load doctors
      const doctorsData = await staffService?.getStaffByRole('doctor');
      console.log('👨‍⚕️ Doctors loaded:', doctorsData);
      setDoctors(doctorsData || []);

      // Load wellness services
      const servicesData = await wellnessService?.getWellnessServices();
      console.log('💆 Services loaded:', servicesData);
      setServices(servicesData || []);

      // Load treatments
      const { data: treatmentsData } = await supabase
        ?.from('treatments')
        ?.select('id, title')
        ?.eq('status', 'published');
      console.log('💊 Treatments loaded:', treatmentsData);
      setTreatments(treatmentsData || []);
    } catch (err) {
      console.error('❌ Error loading dropdown data:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      serviceIds: prev?.serviceIds?.includes(serviceId)
        ? prev?.serviceIds?.filter(id => id !== serviceId)
        : [...prev?.serviceIds, serviceId]
    }));
  };

  // Handle client name search with debouncing
  const handleClientNameChange = async (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, clientName: value }));

    // Only search if 3 or more characters
    if (value.length >= 3) {
      setSearchingClients(true);
      try {
        const results = await clientService.searchClients(value);
        setClientSearchResults(results);
        setShowClientDropdown(results.length > 0);
      } catch (error) {
        console.error('Error searching clients:', error);
        setClientSearchResults([]);
      } finally {
        setSearchingClients(false);
      }
    } else {
      setClientSearchResults([]);
      setShowClientDropdown(false);
    }
  };

  // Handle client selection from dropdown
  const handleClientSelect = (client) => {
    setFormData(prev => ({
      ...prev,
      clientName: client.full_name,
      clientPhone: client.phone,
      clientEmail: client.email || '',
      clientGender: client.gender || ''
    }));
    setShowClientDropdown(false);
    setClientSearchResults([]);
  };



  // Load available therapists when date, time, gender, and service/treatment change
  useEffect(() => {
    const loadAvailableTherapists = async () => {
      // Only load therapists for service and treatment bookings
      if (bookingType !== 'service' && bookingType !== 'treatment') {
        setAvailableTherapists([]);
        return;
      }

      // Need date, time, and gender to check availability
      if (!formData.preferredDate || !formData.preferredTime || !formData.clientGender) {
        setAvailableTherapists([]);
        return;
      }

      setLoadingTherapists(true);
      try {
        let durationMinutes = 60; // Default
        let requiredCount = 1; // Default

        if (bookingType === 'service' && formData.serviceIds.length > 0) {
          // Calculate duration and therapists required for selected services
          const selectedServices = services.filter(s => formData.serviceIds.includes(s.id));
          durationMinutes = Math.max(...selectedServices.map(s => s.duration_minutes || 60));
          requiredCount = Math.max(...selectedServices.map(s => s.therapists_required || 1));
          setTherapistsRequired(requiredCount);
        } else if (bookingType === 'treatment' && formData.treatmentId) {
          // Treatments typically require 1 therapist unless specified otherwise
          requiredCount = 1;
          setTherapistsRequired(requiredCount);
        }

        // Fetch available therapists
        const therapists = await staffService.getAvailableTherapistsForTherapy(
          formData.preferredDate,
          formData.preferredTime,
          formData.clientGender,
          durationMinutes,
          requiredCount
        );

        setAvailableTherapists(therapists);
      } catch (error) {
        console.error('Error loading available therapists:', error);
        setAvailableTherapists([]);
      } finally {
        setLoadingTherapists(false);
      }
    };

    loadAvailableTherapists();
  }, [bookingType, formData.preferredDate, formData.preferredTime, formData.clientGender, formData.serviceIds, formData.treatmentId, services, treatments]);

  // Update therapist warning when selection changes
  useEffect(() => {
    if (bookingType === 'service' && therapistsRequired > 0) {
      if (formData.therapistIds.length < therapistsRequired) {
        setTherapistWarning(
          `This service requires ${therapistsRequired} therapist${therapistsRequired > 1 ? 's' : ''}, but only ${formData.therapistIds.length} ${formData.therapistIds.length === 1 ? 'is' : 'are'} selected. You can proceed with fewer therapists if needed.`
        );
      } else {
        setTherapistWarning('');
      }
    } else {
      setTherapistWarning('');
    }
  }, [bookingType, therapistsRequired, formData.therapistIds]);

  // State for price breakdown
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);

  // Calculate fee with GST and discount
  useEffect(() => {
    const calculateFee = async () => {
      console.log('🔄 Calculating fee...', { bookingType, doctorId: formData?.doctorId, serviceIds: formData?.serviceIds });

      if (bookingType === 'consultation' && formData?.doctorId) {
        setLoadingPrice(true);
        try {
          const doctor = doctors?.find(d => d?.id === formData?.doctorId);
          console.log('👨‍⚕️ Selected doctor:', doctor);

          // Use consultationFee (camelCase) from staffService transformation
          const consultationFee = doctor?.consultationFee || doctor?.consultation_fee || 0;
          console.log('💵 Base consultation fee:', consultationFee);

          const pricing = await calculateConsultationTotal(consultationFee);
          const formatted = formatPriceBreakdown(pricing);
          setPriceBreakdown(formatted);
          console.log('💰 Consultation pricing breakdown:', formatted);
        } catch (error) {
          console.error('❌ Error calculating consultation fee:', error);
          const doctor = doctors?.find(d => d?.id === formData?.doctorId);
          const fallbackFee = doctor?.consultationFee || doctor?.consultation_fee || 0;
          setPriceBreakdown({
            basePrice: fallbackFee.toFixed(2),
            finalAmount: fallbackFee.toFixed(2),
            gstAmount: '0.00',
            gstPercentage: null,
            discountPercentage: '0.00',
            discountAmount: '0.00',
            discountReason: null,
            hasGst: false,
            hasDiscount: false,
            savings: '0.00'
          });
        } finally {
          setLoadingPrice(false);
        }
      } else if (bookingType === 'service' && formData?.serviceIds?.length > 0) {
        setLoadingPrice(true);
        try {
          const selectedServices = services?.filter(s => formData?.serviceIds?.includes(s?.id));
          console.log('💆 Selected services:', selectedServices);

          const pricing = await calculateWellnessTotal(selectedServices);
          const formatted = formatPriceBreakdown(pricing);
          setPriceBreakdown(formatted);
          console.log('💰 Wellness pricing breakdown:', formatted);
        } catch (error) {
          console.error('❌ Error calculating wellness fee:', error);
          const selectedServices = services?.filter(s => formData?.serviceIds?.includes(s?.id));
          const total = selectedServices?.reduce((sum, s) => sum + (s?.base_price || 0), 0);
          setPriceBreakdown({
            basePrice: total.toFixed(2),
            finalAmount: total.toFixed(2),
            gstAmount: '0.00',
            gstPercentage: null,
            discountPercentage: '0.00',
            discountAmount: '0.00',
            discountReason: null,
            hasGst: false,
            hasDiscount: false,
            savings: '0.00'
          });
        } finally {
          setLoadingPrice(false);
        }
      } else {
        console.log('⚠️ No pricing to calculate - resetting to null');
        setPriceBreakdown(null);
      }
    };

    calculateFee();
  }, [bookingType, formData?.doctorId, formData?.serviceIds, doctors, services]);

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      console.log('📝 Submitting appointment...', { bookingType, formData, priceBreakdown });

      // Validate required fields (email is NOT required)
      if (!formData?.clientName) {
        throw new Error('Client name is required');
      }

      if (!formData?.clientPhone) {
        throw new Error('Client phone number is required');
      }

      if (!formData?.clientGender) {
        throw new Error('Client gender is required');
      }

      if (!formData?.preferredDate || !formData?.preferredTime) {
        throw new Error('Appointment date and time are required');
      }

      if (bookingType === 'consultation' && !formData?.doctorId) {
        throw new Error('Please select a doctor');
      }

      if (bookingType === 'service') {
        if (!formData?.serviceIds || formData?.serviceIds?.length === 0) {
          throw new Error('Please select at least one service');
        }
        // Show confirmation dialog if fewer therapists selected than required (admin override)
        if (therapistsRequired > 0 && formData.therapistIds.length < therapistsRequired && formData.therapistIds.length > 0) {
          const confirmed = window.confirm(
            `This service requires ${therapistsRequired} therapist${therapistsRequired > 1 ? 's' : ''}, but only ${formData.therapistIds.length} ${formData.therapistIds.length === 1 ? 'is' : 'are'} selected.\n\nDo you want to proceed with fewer therapists?`
          );
          if (!confirmed) {
            setLoading(false);
            return;
          }
        }
      }

      if (bookingType === 'treatment') {
        if (!formData?.treatmentId) {
          throw new Error('Please select a treatment');
        }
        if (!formData?.doctorId) {
          throw new Error('Please select the prescribing doctor');
        }
      }

      // Prepare appointment data based on booking type
      const appointmentData = {
        bookingType: bookingType, // Add booking type to the data
        details: {
          name: formData?.clientName,
          email: formData?.clientEmail,
          phone: formData?.clientPhone,
          gender: formData?.clientGender,
          chiefComplaint: formData?.chiefComplaint,
          medicalHistory: formData?.medicalHistory,
          currentMedications: formData?.currentMedications,
          severity: formData?.severity,
          consultationMode: formData?.consultationMode
        },
        dateTime: {
          date: formData?.preferredDate,
          timeSlot: formData?.preferredTime
        },
        payment: {
          method: formData?.paymentMethod || 'clinic',
          status: formData?.paymentStatus || 'pending'
        },
        notes: formData?.notes,
        // Include pricing breakdown for record keeping
        pricing: priceBreakdown ? {
          basePrice: parseFloat(priceBreakdown.basePrice),
          gstAmount: parseFloat(priceBreakdown.gstAmount || 0),
          discountAmount: parseFloat(priceBreakdown.discountAmount || 0),
          finalAmount: parseFloat(priceBreakdown.finalAmount)
        } : null
      };

      if (bookingType === 'consultation') {
        const doctor = doctors?.find(d => d?.id === formData?.doctorId);
        console.log('👨‍⚕️ Booking consultation with doctor:', doctor);
        appointmentData.doctor = {
          userId: doctor?.userId || doctor?.id,
          consultationFee: parseFloat(priceBreakdown?.finalAmount || doctor?.consultationFee || doctor?.consultation_fee || 0)
        };
      } else if (bookingType === 'service') {
        const selectedServices = services?.filter(s => formData?.serviceIds?.includes(s?.id));
        console.log('💆 Booking wellness services:', selectedServices);
        appointmentData.services = selectedServices;
        appointmentData.treatmentFee = parseFloat(priceBreakdown?.finalAmount || 0);

        // Add therapists if selected
        if (formData.therapistIds.length > 0) {
          appointmentData.therapists = formData.therapistIds.map((id, index) => ({
            id,
            staffId: id,
            role: index === 0 ? 'primary' : 'assistant'
          }));
        }
      } else if (bookingType === 'treatment') {
        appointmentData.treatmentId = formData.treatmentId;

        // Add prescribing doctor
        const doctor = doctors?.find(d => d?.id === formData?.doctorId);
        if (doctor) {
          appointmentData.doctor = {
            userId: doctor?.userId || doctor?.id
          };
        }

        // Add therapists if selected
        if (formData.therapistIds.length > 0) {
          appointmentData.therapists = formData.therapistIds.map((id, index) => ({
            id,
            staffId: id,
            role: index === 0 ? 'primary' : 'assistant'
          }));
        }
      }

      console.log('📤 Final appointment data:', appointmentData);

      // Create appointment
      const result = await appointmentService?.createAppointment(appointmentData);
      console.log('✅ Appointment created successfully:', result);

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('❌ Error creating appointment:', err);
      setError(err?.message || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e?.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Create New Booking</h2>
              <p className="text-blue-100 text-sm mt-1">Admin booking creation for clients</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Step 1: Booking Type Selection */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                Booking Type
              </h3>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { value: 'consultation', label: 'Doctor Consultation', icon: User },
                  { value: 'service', label: 'Wellness Service', icon: Calendar },
                  { value: 'treatment', label: 'Prescribed Treatment', icon: FileText }
                ]?.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    onClick={() => setBookingType(value)}
                    className={`p-4 rounded-lg border-2 transition-all ${bookingType === value
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                      }`}
                  >
                    <Icon className="w-6 h-6 mx-auto mb-2" />
                    <p className="text-sm font-medium">{label}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Client Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <User className="w-5 h-5 mr-2 text-blue-600" />
                Client Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {/* Client Name with Autocomplete */}
                <div className="space-y-2 relative">
                  <label className="text-sm font-medium text-gray-700">Full Name *</label>
                  <div className="relative">
                    <input
                      type="text"
                      name="clientName"
                      value={formData?.clientName}
                      onChange={handleClientNameChange}
                      onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                      onFocus={() => formData?.clientName?.length >= 3 && clientSearchResults.length > 0 && setShowClientDropdown(true)}
                      required
                      placeholder="Type name or phone (min 3 chars)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {searchingClients && (
                      <div className="absolute right-3 top-2.5">
                        <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                      </div>
                    )}
                  </div>

                  {/* Autocomplete Dropdown */}
                  {showClientDropdown && clientSearchResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {clientSearchResults.map((client) => (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => handleClientSelect(client)}
                          className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{client.full_name}</p>
                              <p className="text-sm text-gray-600">{client.phone}</p>
                              {client.email && <p className="text-xs text-gray-500">{client.email}</p>}
                            </div>
                            <div className="text-right">
                              <span className={`text-xs px-2 py-1 rounded-full ${client.gender === 'Male' ? 'bg-blue-100 text-blue-700' :
                                client.gender === 'Female' ? 'bg-pink-100 text-pink-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                {client.gender || 'N/A'}
                              </span>
                              {client.total_appointments > 0 && (
                                <p className="text-xs text-gray-500 mt-1">{client.total_appointments} visits</p>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <Input
                  label="Email"
                  name="clientEmail"
                  type="email"
                  value={formData?.clientEmail}
                  onChange={handleInputChange}
                  placeholder="client@example.com"
                />
                <Input
                  label="Phone"
                  name="clientPhone"
                  type="tel"
                  value={formData?.clientPhone}
                  onChange={handleInputChange}
                  required
                  placeholder="+91 98765 43210"
                />
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Gender *</label>
                  <select
                    name="clientGender"
                    value={formData?.clientGender}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Step 3: Type-Specific Selection */}
            {bookingType === 'consultation' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Select Doctor</h3>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Doctor *</label>
                  <select
                    name="doctorId"
                    value={formData?.doctorId || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a doctor</option>
                    {doctors?.map(doctor => (
                      <option key={doctor?.id} value={doctor?.id}>
                        {doctor?.fullName || doctor?.full_name || 'Unknown Doctor'} - ₹{doctor?.consultationFee || doctor?.consultation_fee || 0}
                      </option>
                    ))}
                  </select>
                  {doctors?.length === 0 && (
                    <p className="text-sm text-gray-500 mt-1">No doctors available. Please add doctors in staff management.</p>
                  )}
                </div>
              </div>
            )}

            {bookingType === 'service' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Select Services</h3>
                {services?.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3 max-h-60 overflow-y-auto p-2">
                    {services?.map(service => (
                      <label
                        key={service?.id}
                        className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${formData?.serviceIds?.includes(service?.id)
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData?.serviceIds?.includes(service?.id)}
                          onChange={() => handleServiceToggle(service?.id)}
                          className="mr-2"
                        />
                        <div className="inline-block">
                          <span className="font-medium block">{service?.name || service?.title}</span>
                          <span className="text-sm text-gray-600">₹{service?.base_price || 0}</span>
                          {service?.therapists_required > 1 && (
                            <span className="block text-xs text-orange-600 mt-1">
                              Requires {service.therapists_required} therapists
                            </span>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
                    <p className="text-sm text-gray-500">No wellness services available. Please add services first.</p>
                  </div>
                )}
                {formData?.serviceIds?.length > 0 && (
                  <p className="text-sm text-blue-600 font-medium">
                    {formData.serviceIds.length} service{formData.serviceIds.length > 1 ? 's' : ''} selected
                    {therapistsRequired > 1 && ` • Requires ${therapistsRequired} therapists`}
                  </p>
                )}
              </div>
            )}

            {bookingType === 'treatment' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Prescribed Treatment Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Treatment *</label>
                    <select
                      name="treatmentId"
                      value={formData?.treatmentId || ''}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a treatment</option>
                      {treatments?.map(treatment => (
                        <option key={treatment?.id} value={treatment?.id}>
                          {treatment?.title}
                        </option>
                      ))}
                    </select>
                    {treatments?.length === 0 && (
                      <p className="text-sm text-gray-500 mt-1">No treatments available.</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Prescribed By *</label>
                    <select
                      name="doctorId"
                      value={formData?.doctorId || ''}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select prescribing doctor</option>
                      {doctors?.map(doctor => (
                        <option key={doctor?.id} value={doctor?.id}>
                          Dr. {doctor?.fullName || doctor?.full_name || 'Unknown Doctor'}
                        </option>
                      ))}
                    </select>
                    {doctors?.length === 0 && (
                      <p className="text-sm text-gray-500 mt-1">No doctors available.</p>
                    )}
                  </div>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> This is for booking a prescribed treatment. The prescribing doctor is the one who recommended this treatment to the patient.
                  </p>
                </div>
              </div>
            )}

            {/* Step 4: Appointment Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                Appointment Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Preferred Date"
                  name="preferredDate"
                  type="date"
                  value={formData?.preferredDate}
                  onChange={handleInputChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                />
                <Input
                  label="Preferred Time"
                  name="preferredTime"
                  type="time"
                  value={formData?.preferredTime}
                  onChange={handleInputChange}
                  required
                />
              </div>
              {timeSlotWarning && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">{timeSlotWarning}</p>
                </div>
              )}

              {/* Therapist Selection - Positioned after time selection */}
              {(bookingType === 'service' || bookingType === 'treatment') &&
                formData?.preferredDate &&
                formData?.preferredTime &&
                formData?.clientGender && (
                  <div className="space-y-2">
                    <MultiSelectDropdown
                      options={availableTherapists}
                      selectedIds={formData.therapistIds}
                      onChange={(selectedIds) => setFormData(prev => ({ ...prev, therapistIds: selectedIds }))}
                      placeholder={`Select Therapists${therapistsRequired > 0 ? ` (${therapistsRequired} required)` : ''}`}
                      label="Therapists"
                      requiredCount={therapistsRequired}
                      searchable={true}
                      loading={loadingTherapists}
                      disabled={availableTherapists.length === 0}
                      renderOption={(therapist) => (
                        <div className="flex items-center justify-between w-full">
                          <span className="text-sm font-medium">{therapist.fullName}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${therapist.gender === 'Male'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-pink-100 text-pink-700'
                            }`}>
                            {therapist.gender}
                          </span>
                        </div>
                      )}
                      renderSelectedTag={(therapist) => (
                        <span className="flex items-center gap-1">
                          {therapist.fullName}
                          <span className={`text-xs px-1 py-0.5 rounded ${therapist.gender === 'Male'
                            ? 'bg-blue-200 text-blue-800'
                            : 'bg-pink-200 text-pink-800'
                            }`}>
                            {therapist.gender}
                          </span>
                        </span>
                      )}
                    />
                    {availableTherapists.length === 0 && !loadingTherapists && (
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                          {formData.clientGender
                            ? `No ${formData.clientGender.toLowerCase()} therapists available for the selected date and time.`
                            : 'Please select client gender, date, and time to see available therapists.'}
                        </p>
                      </div>
                    )}
                    {/* Warning banner for insufficient therapists */}
                    {therapistWarning && (
                      <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg flex items-start gap-2">
                        <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <p className="text-sm text-yellow-800">{therapistWarning}</p>
                      </div>
                    )}
                  </div>
                )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Consultation Mode</label>
                  <select
                    name="consultationMode"
                    value={formData?.consultationMode}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="in_person">In Person</option>
                    <option value="virtual">Virtual</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Severity</label>
                  <select
                    name="severity"
                    value={formData?.severity}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="mild">Mild</option>
                    <option value="moderate">Moderate</option>
                    <option value="severe">Severe</option>
                  </select>
                </div>
              </div>
              <Input
                label="Chief Complaint"
                name="chiefComplaint"
                value={formData?.chiefComplaint}
                onChange={handleInputChange}
                placeholder="Main reason for visit"
              />
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  name="notes"
                  value={formData?.notes}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Additional notes or instructions"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Step 5: Payment Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <DollarSign className="w-5 h-5 mr-2 text-blue-600" />
                Payment Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Payment Method</label>
                  <select
                    name="paymentMethod"
                    value={formData?.paymentMethod}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="clinic">Pay at Clinic</option>
                    <option value="online">Online Payment</option>
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Payment Status</label>
                  <select
                    name="paymentStatus"
                    value={formData?.paymentStatus}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
              </div>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                {loadingPrice ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    <span className="ml-2 text-sm text-gray-600">Calculating fee...</span>
                  </div>
                ) : priceBreakdown ? (
                  <>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Base Fee:</span>
                      <span className="text-gray-800">₹{priceBreakdown.basePrice}</span>
                    </div>
                    {priceBreakdown.hasGst && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">GST ({priceBreakdown.gstPercentage}%):</span>
                        <span className="text-gray-800">₹{priceBreakdown.gstAmount}</span>
                      </div>
                    )}
                    {priceBreakdown.hasDiscount && (
                      <div className="flex items-center justify-between text-sm text-green-600">
                        <span>Discount ({priceBreakdown.discountPercentage}%):</span>
                        <span>-₹{priceBreakdown.discountAmount}</span>
                      </div>
                    )}
                    <div className="pt-2 border-t border-blue-300">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Total Fee:</span>
                        <span className="text-2xl font-bold text-blue-600">₹{priceBreakdown.finalAmount}</span>
                      </div>
                    </div>
                    {priceBreakdown.hasDiscount && priceBreakdown.discountReason && (
                      <p className="text-xs text-green-600 mt-1">🎉 {priceBreakdown.discountReason}</p>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Total Fee:</span>
                    <span className="text-2xl font-bold text-blue-600">₹0</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 p-6 rounded-b-2xl border-t border-gray-200 flex items-center justify-end space-x-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || !formData?.clientName || !formData?.clientPhone || !formData?.preferredDate || !formData?.preferredTime}
              loading={loading}
              iconName="Save"
              iconPosition="left"
            >
              {loading ? 'Creating...' : 'Create Booking'}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AdminBookingModal;
